import api from '../../../api';

/**
 * Helper: format date as dd/mm/yyyy
 */
const formatDateToDDMMYYYY = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

/**
 * Send task assignment email to assigned user
 * @param {Object} emailData - Email data with to, subject, message, taskId
 * @returns {Promise} - API response
 */
export const sendTaskAssignmentEmail = async (emailData) => {
  try {
    console.log('=== DEBUG: API Send Task Assignment Email ===');
    console.log('Email data to send:', emailData);
    
    const response = await api.post('/crm/tasks/send-email', emailData);
    console.log('Email API response:', response);
    
    return response.data;
  } catch (error) {
    console.error('Error sending task assignment email:', error);
    throw error;
  }
};

/**
 * Get task email templates
 * @returns {Promise<Array>} - Array of email templates
 */
export const getTaskEmailTemplates = async () => {
  try {
    const response = await api.get('/email/templates/template-id/task');
    
    // Handle different response formats
    let templates = [];
    if (response.data) {
      if (Array.isArray(response.data)) {
        templates = response.data;
      } else if (response.data.content && Array.isArray(response.data.content)) {
        templates = response.data.content;
      } else if (typeof response.data === 'object') {
        // Single template object - wrap in array
        templates = [response.data];
      }
    }
    
    return templates;
  } catch (error) {
    console.error('Error fetching task email templates:', error);
    // Return empty array if no templates found
    return [];
  }
};

/**
 * Check email settings
 * @returns {Promise<Object|null>} - Email settings or null
 */
export const checkEmailSettings = async () => {
  try {
    const response = await api.get('/email/settings');
    return response.data;
  } catch (error) {
    console.error('Error checking email settings:', error);
    return null;
  }
};

/**
 * Send task assignment email to user with template
 * @param {string} userEmail - Email of the assigned user
 * @param {Object} emailSettings - Email configuration settings
 * @param {Array} emailTemplates - Available email templates
 * @param {Object} taskData - Task data for email placeholders
 * @returns {Promise} - Email send result
 */
export const sendTaskEmailToUser = async (userEmail, emailSettings, emailTemplates, taskData) => {
  try {
    console.log('=== DEBUG: Sending Task Email ===');
    console.log('User Email:', userEmail);
    console.log('Email Settings:', emailSettings);
    console.log('Email Templates:', emailTemplates);
    console.log('Task Data:', taskData);

    // Check if email settings are configured
    if (!emailSettings) {
      console.warn('⚠️ Email settings not configured');
      throw new Error('Email settings not configured');
    }

    // Check if user email is provided
    if (!userEmail) {
      console.warn('⚠️ User email not provided');
      throw new Error('User email not provided');
    }

    // Get email template for tasks
    let emailTemplate = null;
    if (Array.isArray(emailTemplates) && emailTemplates.length > 0) {
      emailTemplate = emailTemplates.find(template =>
        template.templateId === 'task' ||
        template.id === 'task'
      );
    }

    // Prepare email content with defaults
    let subject = `New Task Assigned: ${taskData.taskTitle || 'Task'}`;
    const defaultTaskLink = taskData.taskLink || taskData.task_link || (taskData.companyId ? `${window.location.origin}/clients/${taskData.companyId}` : '');
    let message = `Dear ${taskData.assignedUser || 'User'},<br><br>You have been assigned a new task:<br><br>Task Title: ${taskData.taskTitle || 'N/A'}<br>Type: ${taskData.type || 'N/A'}<br>Priority: ${taskData.priority || 'N/A'}<br>Status: ${taskData.status || 'N/A'}<br>Description: ${taskData.description || 'N/A'}<br>Due Date: ${formatDateToDDMMYYYY(taskData.dueDate) || 'N/A'}<br>Client: ${taskData.clientName || 'N/A'}<br>Project: ${taskData.projectName || 'N/A'}${defaultTaskLink ? `<br><br><a href="${defaultTaskLink}" target="_blank" rel="noopener noreferrer">Link Task</a>` : ''}<br><br>Please log in to the system to view and manage this task.<br><br>Best regards,<br>CRM System`;

    // Use template if available and replace placeholders
    if (emailTemplate) {
      subject = emailTemplate.subject || subject;
      let templateContent = emailTemplate.content || message;

      // Replace placeholders with actual task data
      templateContent = templateContent
        .replace(/\{\{assigned_user\}\}/g, taskData.assignedUser || 'User')
        .replace(/\{\{task_title\}\}/g, taskData.taskTitle || 'N/A')
        .replace(/\{\{task_type\}\}/g, taskData.type || 'N/A')
        .replace(/\{\{priority\}\}/g, taskData.priority || 'N/A')
        .replace(/\{\{status\}\}/g, taskData.status || 'N/A')
        .replace(/\{\{description\}\}/g, taskData.description || 'N/A')
        .replace(/\{\{due_date\}\}/g, formatDateToDDMMYYYY(taskData.dueDate) || 'N/A')
        .replace(/\{\{client_name\}\}/g, taskData.clientName || 'N/A')
        .replace(/\{\{project_name\}\}/g, taskData.projectName || 'N/A')
        .replace(/\{\{contact_name\}\}/g, taskData.contactName || 'N/A')
        .replace(/\{\{created_by\}\}/g, taskData.createdBy || 'System')
        .replace(/\{\{created_at\}\}/g, formatDateToDDMMYYYY(taskData.createdAt) || formatDateToDDMMYYYY(new Date()))
        .replace(/\{\{company_id\}\}/g, taskData.companyId || 'N/A')
        .replace(/\{\{task_link\}\}/g, defaultTaskLink || '#');

      message = templateContent;
      
      // Also replace placeholders in subject
      subject = subject
        .replace(/\{\{assigned_user\}\}/g, taskData.assignedUser || 'User')
        .replace(/\{\{task_title\}\}/g, taskData.taskTitle || 'N/A')
        .replace(/\{\{task_type\}\}/g, taskData.type || 'N/A')
        .replace(/\{\{priority\}\}/g, taskData.priority || 'N/A')
        .replace(/\{\{client_name\}\}/g, taskData.clientName || 'N/A')
        .replace(/\{\{project_name\}\}/g, taskData.projectName || 'N/A');
    }

    // Prepare email data
    const emailData = {
      to: userEmail,
      subject: subject,
      message: message,
      taskId: taskData.id,
      includeAttachment: false
    };

    console.log('=== DEBUG: Email Data Prepared ===');
    console.log('Email Data:', emailData);

    // Send email
    const result = await sendTaskAssignmentEmail(emailData);
    
    console.log('✅ Task email sent successfully');
    return result;
  } catch (error) {
    console.error('✗ Error sending task email:', error);
    throw error;
  }
};









